import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../../utils';
import { Brain } from 'lucide-react';
import { AnimatedDots } from '../shared';

// ============================================================================
// ActivityIndicator - Unified timeline indicator for typing/thinking states
// ============================================================================

export interface ActivityIndicatorProps extends HTMLAttributes<HTMLDivElement> {
  /** Who is active: 'agent' for AI thinking, 'user' for human typing */
  actor: 'agent' | 'user';
  /** Display name */
  name?: string;
  /** Optional activity description (e.g., "Analyzing data...", "Typing...") */
  activity?: string;
  /** Custom icon to override default */
  icon?: ReactNode;
  /** Whether this is the last item in timeline (hides connector) */
  isLast?: boolean;
  /** User initials for user actor (default: derived from name) */
  initials?: string;
  /** Whether this is the current user (affects user styling) */
  isCurrentUser?: boolean;
}

export const ActivityIndicator = forwardRef<HTMLDivElement, ActivityIndicatorProps>(
  ({
    className,
    actor,
    name,
    activity,
    icon,
    isLast,
    initials,
    isCurrentUser,
    ...props
  }, ref) => {
    const isAgent = actor === 'agent';

    // Default names
    const displayName = name || (isAgent ? 'Assistant' : 'You');

    // Derive initials for user
    const displayInitials = initials || displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

    // For user actor, default isCurrentUser based on name
    const isSelf = isCurrentUser ?? displayName === 'You';

    // Timeline indicator styling
    const indicatorBgClass = isAgent
      ? 'bg-purple-100 dark:bg-purple-900/50'
      : isSelf
        ? 'bg-sky-100 dark:bg-sky-900/50'
        : 'bg-violet-100 dark:bg-violet-900/50';

    // Default icon
    const defaultIcon = isAgent ? (
      <Brain className="h-4 w-4 text-purple-500 dark:text-purple-400 animate-pulse" />
    ) : (
      <span className={cn(
        'text-xs font-medium',
        isSelf
          ? 'text-sky-600 dark:text-sky-400'
          : 'text-violet-600 dark:text-violet-400'
      )}>
        {displayInitials}
      </span>
    );

    // Default activity text
    const defaultActivity = isAgent ? 'Thinking' : 'typing';

    return (
      <div ref={ref} className={cn('flex items-start gap-3', className)} {...props}>
        {/* Timeline indicator */}
        <div className="flex flex-col items-center">
          <div className={cn(
            'flex items-center justify-center h-8 w-8 rounded-full shrink-0',
            indicatorBgClass
          )}>
            {icon || defaultIcon}
          </div>
          {!isLast && (
            <div className="w-px flex-1 bg-slate-200 dark:bg-slate-700 min-h-[16px]" />
          )}
        </div>

        {/* Content */}
        <div className={cn('flex-1 min-h-8 flex items-center px-3 -ml-1', !isLast && 'pb-4')}>
          <div className="flex flex-col">
            <div className="flex items-center">
              <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                {displayName}
              </span>
              <AnimatedDots variant={actor} className="ml-1.5" />
            </div>
            <span className="text-sm text-slate-500 dark:text-slate-400">
              {activity || defaultActivity}
            </span>
          </div>
        </div>
      </div>
    );
  }
);
ActivityIndicator.displayName = 'ActivityIndicator';
